import { KeyboardReturn, Visibility } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { RETURN_PURCHASE } from "../../constants";
import { PageLayout } from "../../layouts/PageLayout";
import { PurchaseService } from "../../services";
import { useHasPermission } from "../../utils";
import { getDateAndTime } from "../../utils/formatters";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface PurchaseReturn {
  id: string;
  status: string;
}

interface PurchaseItem {
  purchaseItemId: string;
  price: string | number;
  quantity: string | number;
  totalAmount: string | number;
  taxPercentage?: string | number;
}

interface PurchaseInvoiceData {
  purchaseReturns?: PurchaseReturn[];
  vendorId?: string;
  discountAmount?: string | number;
  purchaseInvoiceNumber?: string;
  receivedDate?: string;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  company?: { name: string };
  vendor?: { name: string; primaryPhone?: string };
  items?: PurchaseItem[];
  totalInvoiceAmount?: string | number;
}

export const PurchaseInvoice = () => {
  const [invoiceData, setInvoiceData] = useState<Partial<PurchaseInvoiceData>>({ items: [] });
  const [items, setItems] = useState([]);

  const hasPurchaseReturn = useHasPermission(RETURN_PURCHASE);

  const purchaseReturns = invoiceData?.purchaseReturns || [];
  const hasActiveReturn = purchaseReturns.some((r) => r.status !== "CANCELLED");
  const activeReturn = purchaseReturns.find((r) => r.status !== "CANCELLED");
  const canCreateReturn = !hasActiveReturn;

  const printRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { invoiceId } = useParams();

  useEffect(() => {
    fetchData(invoiceId);
  }, [invoiceId]);

  const fetchData = async (id) => {
    try {
      const { data: response } = await PurchaseService.getPurchaseById(id);
      setInvoiceData(response);

      const items = Array.from(new Set(response?.items?.map((item) => item.purchaseItemId)));
      const { data: purchaseItems } = await PurchaseService.getPurchaseItems({ ids: items.join(",") });
      setItems(purchaseItems?.rows || []);
    } catch (error) {
      toast.error("Failed to fetch invoice data");
      console.error("Error fetching invoice data:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const handleRetunClick = () => {
    navigate(`/vendor/${invoiceData?.vendorId}/invoice/${invoiceId}/return`);
  };

  const handleShowReturnInvoice = () => {
    navigate(`/vendor/${invoiceData?.vendorId}/return/${activeReturn?.id}`);
  };

  const rows = invoiceData?.items?.map((item, index) => ({
    sl: index + 1,
    item: items.find((i) => i.id === item?.purchaseItemId)?.name || "N/A",
    rate: item?.price || "-",
    quantity: item?.quantity || "-",
    tax: `${item.taxPercentage || "-"}%`,
    total: item?.totalAmount || "-",
  }));

  const buttons = [
    hasPurchaseReturn &&
      canCreateReturn && {
        buttonText: "Return Purchase",
        buttonIcon: KeyboardReturn,
        onClick: handleRetunClick,
      },
    hasActiveReturn && {
      buttonText: "View Return Invoice",
      buttonIcon: Visibility,
      onClick: handleShowReturnInvoice,
    },
  ];

  const rawTotalAmount = useMemo(() => {
    return invoiceData?.items?.reduce(
      (total = 0, item) =>
        total +
        Number(item?.price || 0) * Number(item?.quantity || 0) +
        (Number(item?.taxPercentage || 0) / 100) * Number(item?.price || 0) * Number(item?.quantity || 0),
      0,
    );
  }, [invoiceData?.items]);

  const discountAmount = Number(invoiceData?.discountAmount || 0);

  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Purchase Invoice" buttons={buttons} />
      </Box>

      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <div ref={printRef}>
            <Invoice
              title="Purchase Invoice"
              sectionA={[
                { label: "Invoice No", value: invoiceData?.purchaseInvoiceNumber },
                { label: "Date", value: get_date(invoiceData?.receivedDate, "DD-MM-YYYY") },
              ]}
              sectionB={[
                {
                  label: "Created By",
                  value: invoiceData?.createdBy
                    ? `${invoiceData?.createdBy?.firstName} ${invoiceData?.createdBy?.lastName} (${getDateAndTime(
                        invoiceData?.createdAt,
                      )})`
                    : "N/A",
                },
                invoiceData?.updatedBy && {
                  label: "Last Updated By",
                  value: `${invoiceData?.updatedBy?.firstName ?? ""} ${invoiceData?.updatedBy?.lastName ?? ""} (${getDateAndTime(
                    invoiceData?.updatedAt,
                  )})`,
                },
              ].filter(Boolean)}
              sectionC={[{ label: invoiceData?.company?.name, value: "" }]}
              sectionD={[
                {
                  label: "Bill To",
                  value: (
                    <>
                      {invoiceData?.vendor?.name}
                      <br />
                      {invoiceData?.vendor?.primaryPhone}
                    </>
                  ),
                },
              ]}
              columns={[
                { id: "sl", label: "Sl No" },
                { id: "item", label: "Item" },
                { id: "rate", label: "Rate" },
                { id: "quantity", label: "Quantity" },
                { id: "tax", label: "Tax %" },
                { id: "total", label: "Total" },
              ]}
              rows={rows}
              subtotal={rawTotalAmount}
              discountAmount={discountAmount > 0 ? discountAmount : undefined}
              total={invoiceData?.totalInvoiceAmount}
            />
          </div>
        </CardLayout>
      </Box>

      <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"PurchaseInvoice.pdf"} />
    </PageLayout>
  );
};
